import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge } from '../ui';
import { isAiGeneratedCard } from './aiGenerated.js';
import './CardRenderer.css';

/** Art. 50 Abs. 2 AI Act: the same spelled-out badge DeckManager's card list uses. */
function CardAiBadge({ card, t }) {
  if (!isAiGeneratedCard(card)) return null;
  return (
    <Badge tone="brand" title={t('learn.aiBadgeLabel')}>
      <span aria-hidden="true">{t('learn.aiBadge')}</span>
      <span className="etch-visually-hidden">{t('learn.aiBadgeLabel')}</span>
    </Badge>
  );
}

function shuffleWithSeed(arr, seed) {
  const result = [...arr];
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = ((h << 5) - h + seed.charCodeAt(i)) | 0;
  for (let i = result.length - 1; i > 0; i--) {
    h = (h * 16807) % 2147483647;
    const j = Math.abs(h) % (i + 1);
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
}

function CardImage({ imageUrl }) {
  if (!imageUrl) return null;
  const src = imageUrl.startsWith('/') ? imageUrl : `/api/media/${imageUrl}`;
  return <img src={src} alt="" className="card-image" loading="lazy" />;
}

/**
 * Try to extract A) B) C) D) options inline-embedded in the front text. Returns
 * { question, options } where options is [{label,text}] or null if no pattern matched.
 * Supports "A)", "A.", "A:" — common LLM-output variants.
 */
function parseInlineOptions(front) {
  if (!front || typeof front !== 'string') return null;
  // Match a letter A–E followed by ) . or :
  const re = /\b([A-E])[).:]\s+/g;
  const markers = [];
  let m;
  while ((m = re.exec(front)) !== null) {
    markers.push({ label: m[1], start: m.index, contentStart: re.lastIndex });
  }
  if (markers.length < 2) return null;

  const question = front.slice(0, markers[0].start).trim().replace(/[?.!]?$/, '?');
  const options = markers.map((mk, i) => {
    const end = i + 1 < markers.length ? markers[i + 1].start : front.length;
    return { label: mk.label, text: front.slice(mk.contentStart, end).trim() };
  });
  return { question, options };
}

/** Normalise "B) The works council..." style backs by stripping the leading letter marker. */
function stripLetterMarker(s) {
  if (!s) return s;
  return s.replace(/^\s*[A-E][).:]\s+/, '').trim();
}

/** Soft equality check between user-picked option text and the correct answer. */
function matchesCorrect(option, correct) {
  if (!option || !correct) return false;
  const a = stripLetterMarker(option).toLowerCase();
  const b = stripLetterMarker(correct).toLowerCase();
  if (!a || !b) return false;
  if (a === b) return true;
  // Either contains the other (handles "B) X" vs "X" and "X" vs "X. Y")
  return a.includes(b) || b.includes(a);
}

export default function CardRenderer({ card, revealed }) {
  const { t } = useTranslation();
  const [selectedOption, setSelectedOption] = useState(null);

  // Reset local state when card changes (revealed flips back to false in parent)
  useEffect(() => {
    setSelectedOption(null);
  }, [card.id]);

  // v1.3.2: every card is rendered as 4-option MC if metadata supports it.
  // Treat any card with ≥2 distractors OR inline-encoded options as multiple choice,
  // regardless of card_type. This handles legacy decks where the LLM emitted
  // basic/scenario/definition cards but still produced distractors.
  const distractors = card.metadata?.distractors || [];
  const parsed = parseInlineOptions(card.front);
  const canRenderMC = card.card_type !== 'code_trace'
    && card.card_type !== 'cloze'
    && (distractors.length > 0 || (parsed && parsed.options.length >= 2));

  if (card.card_type === 'code_trace') {
    return (
      <div className="card-renderer code-trace" data-ai-generated={isAiGeneratedCard(card) || undefined}>
        <div className="card-front">
          <CardImage imageUrl={card.image_url} />
          <CardAiBadge card={card} t={t} />
          <pre className="code-block">{card.front}</pre>
        </div>
        {revealed && (
          <div className="card-back">
            <div className="card-answer">{card.back}</div>
          </div>
        )}
      </div>
    );
  }

  if (canRenderMC) {
    const correctAnswer = card.back.split('\n')[0];
    const questionText = parsed?.question || card.front;
    const allOptions = distractors.length > 0
      ? shuffleWithSeed([correctAnswer, ...distractors], card.id)
      : parsed.options.map(o => o.text);

    return (
      <div className="card-renderer multiple-choice" data-ai-generated={isAiGeneratedCard(card) || undefined}>
        <div className="card-front">
          <CardImage imageUrl={card.image_url} />
          <CardAiBadge card={card} t={t} />
          <p className="card-question">{questionText}</p>
          <div className="mc-options">
            {allOptions.map((opt, i) => {
              const isSelected = selectedOption === i;
              const isCorrect = revealed && matchesCorrect(opt, correctAnswer);
              const isWrongPick = revealed && isSelected && !isCorrect;
              const cls = [
                'mc-option',
                isSelected ? 'selected' : '',
                revealed && isCorrect ? 'correct' : '',
                isWrongPick ? 'incorrect' : '',
              ].filter(Boolean).join(' ');
              return (
                <button
                  key={i}
                  className={cls}
                  disabled={revealed}
                  onClick={() => !revealed && setSelectedOption(i)}
                >
                  <span className="mc-letter">{String.fromCharCode(65 + i)}.</span>
                  <span className="mc-text">{stripLetterMarker(opt)}</span>
                </button>
              );
            })}
          </div>
        </div>
        {revealed && (() => {
          // Only show the standalone "Richtige Antwort" block if there's more
          // content than just the correct option text — e.g. an explanation.
          // Otherwise it's pure redundancy with the green-highlighted choice.
          const lines = card.back.split('\n').map(l => l.trim()).filter(Boolean);
          const hasExplanation = lines.length > 1;
          if (!hasExplanation) return null;
          const explanation = lines.slice(1).join('\n');
          return (
            <div className="card-back">
              <div className="card-answer-label">Erklärung</div>
              <div className="card-answer">{explanation}</div>
            </div>
          );
        })()}
      </div>
    );
  }

  if (card.card_type === 'cloze') {
    return (
      <div className="card-renderer cloze" data-ai-generated={isAiGeneratedCard(card) || undefined}>
        <div className="card-front">
          <CardImage imageUrl={card.image_url} />
          <CardAiBadge card={card} t={t} />
          <p className="card-question">{revealed ? card.back : card.front}</p>
        </div>
      </div>
    );
  }

  // Legacy fallback: card has no distractors and no inline options.
  // Reveal-only mode (no textarea — v1.3.2 enforces 4-option MC for new cards;
  // old cards without options just show their answer).
  return (
    <div className="card-renderer basic" data-ai-generated={isAiGeneratedCard(card) || undefined}>
      <div className="card-front">
        <CardImage imageUrl={card.image_url} />
        <CardAiBadge card={card} t={t} />
        <p className="card-question">{card.front}</p>
      </div>
      {revealed && (
        <div className="card-back">
          <div className="card-answer-label">{t('learn.correctAnswer')}</div>
          <div className="card-answer">{card.back}</div>
        </div>
      )}
    </div>
  );
}
